'use client';

import React from 'react';
import { Check } from 'lucide-react';
import { css } from 'styled-system/css';

import ShaojiangImage from '@shaojiang/components/ShaojiangImage';
import ShaojiangLink from '@shaojiang/components/ShaojiangLink';
import Tooltip from '@shaojiang/components/Tooltip';
import { Menu } from '@shaojiang/components/ui/menu';
import { Option } from '@shaojiang/types/common';

export type DropdownOption = Option & { disabled?: boolean };

interface Props {
  menus: DropdownOption[];
  activeValue?: Option['value'];
  onChange?: (value: Option['value']) => void;
  /** The trigger. Must be a single element that accepts a ref (e.g. an IconButton). */
  children: React.ReactElement;
  tooltip?: string;
}

export default function ShaojiangDropdown({
  tooltip = 'Click to show the menus',
  menus,
  activeValue = '',
  onChange,
  children,
}: Props) {
  return (
    <Menu.Root
      positioning={{ placement: 'bottom-end' }}
      onSelect={({ value }) => {
        const menu = menus.find((m) => String(m.value) === value);
        if (menu) onChange?.(menu.value);
      }}
    >
      {/* The tooltip gets its own wrapper: both Ark triggers set an `id`, and if they share an
          element the menu can't find its anchor and opens at the top-left of the page. */}
      <Tooltip title={tooltip}>
        <span className={css({ display: 'inline-flex' })}>
          <Menu.Trigger asChild>{children}</Menu.Trigger>
        </span>
      </Tooltip>
      <Menu.Positioner>
        <Menu.Content minW='44'>
          {menus.map((menu) => {
            const selected = activeValue !== '' && activeValue === menu.value;
            return (
              <Menu.Item
                key={menu.value}
                value={String(menu.value)}
                disabled={menu.disabled}
                justifyContent='space-between'
                fontWeight={selected ? 'semibold' : 'medium'}
                _disabled={{ opacity: 1, color: 'fg.muted', cursor: 'default' }}
              >
                <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '2' })}>
                  {menu.icon && (
                    <ShaojiangImage
                      src={menu.icon}
                      alt={String(menu.label) || ''}
                      width={20}
                      height={20}
                    />
                  )}
                  {menu.href ? (
                    <ShaojiangLink href={menu.href}>{menu.label}</ShaojiangLink>
                  ) : (
                    menu.label
                  )}
                </span>
                {selected && <Check size={16} />}
              </Menu.Item>
            );
          })}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
}
